import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { money } from "@/lib/format";
import { FlaskConical, Loader2, Cpu, ShieldAlert, Plus, TriangleAlert, DollarSign } from "lucide-react";

const pctToScale = (p) => (p < 20 ? 1 : p < 40 ? 2 : p < 60 ? 3 : p < 80 ? 4 : 5);

export default function ScenariosTab({ projectId }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(null);
  const { data, isLoading } = useQuery({ queryKey: ["scenarios", projectId], queryFn: async () => (await api.get(`/projects/${projectId}/scenarios`)).data });

  const scenarios = data?.scenarios || [];

  const generate = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/projects/${projectId}/scenarios`);
      qc.setQueryData(["scenarios", projectId], r);
      toast.success(`Generated ${r.scenarios?.length || 0} what-if scenarios`);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const addToRisks = async (s, i) => {
    setAdding(i);
    try {
      await api.post(`/projects/${projectId}/risks`, {
        title: `[What-If] ${s.title}`,
        category: s.category || "Hidden Condition",
        probability: pctToScale(Number(s.probability_pct) || 0),
        impact: 4,
        cost_impact: Number(s.likely_cost) || Number(s.cost_high) || 0,
        mitigation: s.recommendation || "",
        status: "open",
      });
      qc.invalidateQueries({ queryKey: ["risks", projectId] });
      toast.success("Added to Risk Register");
    } catch (e) { toast.error(apiErr(e)); } finally { setAdding(null); }
  };

  if (isLoading) return <div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-semibold flex items-center gap-2"><FlaskConical className="h-4 w-4 text-primary" /> What-If Scenarios</h3>
          <p className="text-sm text-muted-foreground mt-0.5">Model hidden-condition risks — concealed mold, outdated electrical, dry rot, asbestos, plumbing behind walls — with likely cost exposure, and push any into your Risk Register.</p>
        </div>
        <button data-testid="generate-scenarios-btn" onClick={generate} disabled={busy}
          className="inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-4 py-2.5 font-bold hover:bg-orange-600 transition-colors disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Cpu className="h-4 w-4" />} {scenarios.length ? "Regenerate" : "Generate Scenarios"}
        </button>
      </div>

      {scenarios.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border py-16 text-center">
          <FlaskConical className="h-9 w-9 mx-auto text-muted-foreground mb-3" />
          <p className="font-semibold">No scenarios yet</p>
          <p className="text-sm text-muted-foreground">Generate AI what-if scenarios for the hidden conditions most likely to affect this project.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="scenarios-grid">
          {scenarios.map((s, i) => {
            const p = Number(s.probability_pct) || 0;
            const pcls = p >= 60 ? "text-destructive" : p >= 30 ? "text-amber-500" : "text-emerald-500";
            return (
              <div key={i} className="rounded-xl border border-border bg-card p-5 flex flex-col" data-testid={`scenario-${i}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-semibold flex items-center gap-2"><TriangleAlert className="h-4 w-4 text-amber-500 shrink-0" />{s.title}</div>
                    {s.category && <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{s.category}</span>}
                  </div>
                  <div className={`text-right font-mono font-bold ${pcls}`}><div className="text-lg leading-none">{p}%</div><div className="text-[9px] uppercase text-muted-foreground">likely</div></div>
                </div>
                {s.trigger && <p className="text-xs text-muted-foreground mt-2"><span className="font-semibold text-foreground">Trigger:</span> {s.trigger}</p>}
                <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-lg bg-secondary/50 py-2"><div className="text-[9px] font-mono uppercase text-muted-foreground">Low</div><div className="font-mono text-sm tabular-nums">{money(s.cost_low)}</div></div>
                  <div className="rounded-lg bg-primary/10 py-2"><div className="text-[9px] font-mono uppercase text-primary">Likely</div><div className="font-mono text-sm font-bold text-primary tabular-nums">{money(s.likely_cost)}</div></div>
                  <div className="rounded-lg bg-secondary/50 py-2"><div className="text-[9px] font-mono uppercase text-muted-foreground">High</div><div className="font-mono text-sm tabular-nums">{money(s.cost_high)}</div></div>
                </div>
                {s.recommendation && <p className="text-xs text-muted-foreground mt-3 flex-1"><span className="font-semibold text-foreground">Recommendation:</span> {s.recommendation}</p>}
                <button data-testid={`add-to-risk-${i}`} onClick={() => addToRisks(s, i)} disabled={adding === i}
                  className="mt-4 inline-flex items-center justify-center gap-2 rounded-lg border border-border px-3 py-2 text-sm font-semibold hover:bg-secondary transition-colors disabled:opacity-60">
                  {adding === i ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldAlert className="h-4 w-4" />} Add to Risk Register
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
